import type { ComponentProps, ReactNode } from "react";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

// The 12-column form grid the ported item dialogs lay their fields out on.
//
// MUI `Grid container` / `Grid item xs=… md=…`, which is what both islands use
// (`RequestItemIsland.tsx`, `AddVariantIsland.tsx`) and what decides the shape of
// every row in them: a field is full width on a phone and takes its `md` share
// from `md` up. Shared because the two dialogs were spelling the same three span
// strings and the same wrapper twice, and a third dialog would have made three.
// The item edit page lays its form on the same grid (`AssetEditForm.tsx:170-171`,
// `Grid container spacing={2}`).
//
// `data-field` is what lets a test pin the donor's field *order* — the one thing
// about a form grid that is a fact about the source rather than a style.

/**
 * `Grid container spacing={N}` in MUI 9: a `gap` of `N × 8px` on both axes,
 * with no negative margins (the v9 `Grid` is gap-based). Only the spacings the
 * ported forms use — `2` (request item, item edit), `5` and `1.5` (add
 * variant).
 */
const FORM_GRID_SPACING = {
  1.5: "gap-3",
  2: "gap-4",
  5: "gap-10",
} as const;

export interface FormGridProps extends ComponentProps<"div"> {
  spacing: keyof typeof FORM_GRID_SPACING;
  /** Lay the grid on the caller's own element — the dialog's `<form>`. */
  asChild?: boolean;
}

/** `Grid container`: the 12 columns its `FormGridCell`s span. */
export function FormGrid({ spacing, asChild = false, className, ...props }: FormGridProps) {
  const Comp = asChild ? Slot.Root : "div";
  return (
    <Comp {...props} className={cn("grid grid-cols-12", FORM_GRID_SPACING[spacing], className)} />
  );
}

const FORM_GRID_SPANS = {
  /** `xs={12}` */
  full: "col-span-12",
  /** `xs={12} md={6}` */
  half: "col-span-12 md:col-span-6",
  /** `xs={12} md={4}` */
  third: "col-span-12 md:col-span-4",
} as const;

export type FormGridSpan = keyof typeof FORM_GRID_SPANS;

export interface FormGridCellProps {
  /**
   * The `data-slot` the cell reports under — the owning form's name, so one
   * dialog's test cannot match another's cells.
   */
  slot: string;
  /** Which field this cell holds, for the order assertion. */
  field: string;
  span: FormGridSpan;
  className?: string;
  children: ReactNode;
}

export function FormGridCell({ slot, field, span, className, children }: FormGridCellProps) {
  return (
    <div data-slot={slot} data-field={field} className={cn(FORM_GRID_SPANS[span], className)}>
      {children}
    </div>
  );
}
